/**
 * 環境表示バッジコンポーネント
 * ステージング・本番・デモ環境などを視覚的に表示
 */

import React from 'react';
import { getEnvironmentInfo, isDemoMode } from '../../utils/environment';
import { cn } from '../../utils/classNames';

interface EnvironmentBadgeProps {
  /** 表示バリアント */
  variant?: 'compact' | 'full';
  /** カスタムクラス */
  className?: string;
  /** 本番環境でも表示するかどうか */
  showInProduction?: boolean;
}

/**
 * 環境表示バッジコンポーネント
 */
export const EnvironmentBadge: React.FC<EnvironmentBadgeProps> = ({
  variant = 'compact',
  className = '',
  showInProduction = false,
}) => {
  const envInfo = getEnvironmentInfo();

  // 本番環境では通常非表示（showInProductionがtrueの場合のみ表示）
  if (envInfo.type === 'production' && !showInProduction) {
    return null;
  }

  // 不明な環境では非表示
  if (envInfo.type === 'unknown') {
    return null;
  }

  // デモ環境の場合は、デモモードが有効でない場合は非表示
  if (envInfo.type === 'demo' && !isDemoMode()) {
    return null;
  }

  // 色の設定
  const getColorClasses = (color: string) => {
    switch (color) {
      case 'blue':
        return 'text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700';
      case 'green':
        return 'text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900/20 border-green-300 dark:border-green-700';
      case 'orange':
        return 'text-orange-700 dark:text-orange-300 bg-orange-100 dark:bg-orange-900/20 border-orange-300 dark:border-orange-700';
      case 'purple':
        return 'text-purple-700 dark:text-purple-300 bg-purple-100 dark:bg-purple-900/20 border-purple-300 dark:border-purple-700';
      case 'gray':
      default:
        return 'text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-900/20 border-gray-300 dark:border-gray-700';
    }
  };

  const colorClasses = getColorClasses(envInfo.color);

  if (variant === 'compact') {
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1 px-2 py-1 text-xs font-medium border rounded-full',
          colorClasses,
          className
        )}
      >
        <span>{envInfo.icon}</span>
        <span>{envInfo.label}</span>
        {/* ローカル開発時のSupabase接続先表示 */}
        {envInfo.supabaseTarget && (
          <>
            <span className='opacity-75'>→</span>
            <span>{envInfo.supabaseTarget.icon}</span>
            <span>{envInfo.supabaseTarget.label}</span>
          </>
        )}
      </span>
    );
  }

  // フルバリアント（将来の拡張用）
  return (
    <div
      className={cn(
        'inline-flex items-center gap-2 px-3 py-2 text-sm font-medium border rounded-lg',
        colorClasses,
        className
      )}
    >
      <span className='text-lg'>{envInfo.icon}</span>
      <div>
        <div className='font-semibold'>
          {envInfo.label}
          {/* ローカル開発時のSupabase接続先表示 */}
          {envInfo.supabaseTarget && (
            <>
              <span className='opacity-75 mx-1'>→</span>
              <span>{envInfo.supabaseTarget.icon}</span>
              <span className='ml-1'>{envInfo.supabaseTarget.label}</span>
            </>
          )}
        </div>
        {envInfo.projectId && (
          <div className='text-xs opacity-75'>ID: {envInfo.projectId.slice(0, 8)}...</div>
        )}
      </div>
    </div>
  );
};

/**
 * ヘッダー専用の環境バッジ（既存のデモバッジと同じスタイル）
 */
interface HeaderEnvironmentBadgeProps {
  className?: string;
}

export const HeaderEnvironmentBadge: React.FC<HeaderEnvironmentBadgeProps> = ({
  className = '',
}) => {
  const envInfo = getEnvironmentInfo();

  // 本番環境では非表示
  if (envInfo.type === 'production') {
    return null;
  }

  // 不明な環境では非表示
  if (envInfo.type === 'unknown') {
    return null;
  }

  // デモ環境の場合は、デモモードが有効でない場合は非表示
  if (envInfo.type === 'demo' && !isDemoMode()) {
    return null;
  }

  // 環境に応じたアイコンコンポーネント
  const IconComponent = () => {
    // デモ環境の場合は BeakerIcon を使用（既存と同じ）
    if (envInfo.type === 'demo') {
      // BeakerIcon の代替として絵文字を使用
      return <span className='text-xs'>{envInfo.icon}</span>;
    }

    // その他の環境は絵文字を使用
    return <span className='text-xs'>{envInfo.icon}</span>;
  };

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-2 py-1 text-xs font-medium border rounded-full',
        // 環境ごとの色設定（既存のデモスタイルを踏襲）
        envInfo.type === 'demo'
          ? 'text-orange-700 dark:text-orange-300 bg-orange-100 dark:bg-orange-900/20 border-orange-300 dark:border-orange-700'
          : envInfo.type === 'staging'
            ? 'text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700'
            : envInfo.type === 'development'
              ? 'text-purple-700 dark:text-purple-300 bg-purple-100 dark:bg-purple-900/20 border-purple-300 dark:border-purple-700'
              : 'text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-900/20 border-gray-300 dark:border-gray-700',
        className
      )}
    >
      <IconComponent />
      {envInfo.label}
      {/* ローカル開発時のSupabase接続先表示 */}
      {envInfo.supabaseTarget && (
        <>
          <span className='opacity-75'>→</span>
          <span className='text-xs'>{envInfo.supabaseTarget.icon}</span>
          <span>{envInfo.supabaseTarget.label}</span>
        </>
      )}
    </span>
  );
};

export default EnvironmentBadge;
